import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Building2, MapPin, TrendingUp, DollarSign, Newspaper, ExternalLink } from 'lucide-react';
import SEOPageLayout from '../components/SEOPageLayout';

export default function JobsNYC() {
  return (
    <SEOPageLayout
      badge="New York City"
      title="Looking for a New Real Estate Broker in New York? — Up to 90% Commission"
      subtitle="Switch to Krieger Real Estate and keep up to 90% of every commission. No desk fees, no monthly fees. For qualified residential and commercial agents statewide across New York — Manhattan, Brooklyn, Queens, Long Island, and upstate."
      metaTitle="Looking for a New Real Estate Broker in NYC? | Up to 90% Commission | Krieger"
      metaDescription="Switch brokers in New York. Krieger offers up to 90% commission for residential and commercial agents. No monthly fees, no desk fees. Apply today — NYC, Brooklyn, Queens, Long Island."
      metaKeywords="looking for new real estate broker New York, switch real estate brokerage NYC, best real estate broker to join NYC, real estate agent jobs New York, high commission broker NYC, change broker New York, real estate brokerage near me NYC, new broker for agents Manhattan, best commission split NYC, real estate agent looking for broker Brooklyn, join new brokerage New York, transfer real estate license NY, residential agent brokerage NYC, commercial agent brokerage New York, 90 percent commission New York"
      canonicalPath="/real-estate-agent-jobs-nyc"
    >
      <div className="space-y-12">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <img src="https://images.unsplash.com/photo-1651608034107-12b95f5b2088?w=1200&q=80&auto=format&fit=crop" alt="New York City skyline" className="w-full h-56 object-cover" />
          <div className="p-7">
            <h2 className="text-xl font-['Cabinet_Grotesk'] font-bold mb-3 text-slate-900">Why New York Agents Are Switching to Krieger</h2>
            <p className="text-sm text-slate-500 leading-relaxed mb-5">New York is the most competitive real estate market in the country. Whether you sell condos in Manhattan, rent apartments in Brooklyn, or close multifamily deals upstate — Krieger's up-to-90% split means you keep significantly more on every transaction. No desk fees, no monthly charges, statewide coverage.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {['Up to 90/10 commission on all transactions', 'Residential sales, rentals & commercial', 'Statewide coverage across New York', 'RealtyMX listing platform access', 'No physical office requirement — work from anywhere', 'Full compliance with NY DOS regulations'].map(item => (
                <div key={item} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 size={14} className="text-[#002FA7] shrink-0 mt-0.5" /> {item}</div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-xl font-['Cabinet_Grotesk'] font-bold mb-5 text-slate-900">NYC Markets We Serve</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {['Manhattan', 'Brooklyn', 'Queens', 'The Bronx', 'Staten Island', 'Long Island'].map(area => (
              <div key={area} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-3 text-sm text-slate-700">
                <MapPin size={14} className="text-[#002FA7]" /> {area}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-7" data-testid="nyc-news-section">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-[#EFF3FF] rounded-xl flex items-center justify-center">
              <Newspaper size={18} className="text-[#002FA7]" />
            </div>
            <div>
              <h2 className="text-xl font-['Cabinet_Grotesk'] font-bold text-slate-900">New York Commercial Real Estate News</h2>
              <p className="text-xs text-slate-400">Stay informed on the NYC market with these leading industry sources.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { name: 'The Real Deal', url: 'https://therealdeal.com/new-york/', desc: 'Breaking NYC real estate news, deals, and market analysis' },
              { name: 'Commercial Observer', url: 'https://commercialobserver.com/', desc: 'Office, retail, and investment sales coverage across New York' },
              { name: 'Crain\'s New York Business — Real Estate', url: 'https://www.crainsnewyork.com/real-estate', desc: 'NYC business and commercial property market reporting' },
              { name: 'Bisnow New York', url: 'https://www.bisnow.com/new-york', desc: 'Commercial real estate events, news, and deal flow in NY' },
              { name: 'NY Post — Real Estate', url: 'https://nypost.com/real-estate/', desc: 'Real estate deals, development, and market trends in New York' },
              { name: 'GlobeSt. — New York', url: 'https://www.globest.com/markets/new-york/', desc: 'National CRE news with NY market-specific coverage' },
            ].map(source => (
              <a key={source.name} href={source.url} target="_blank" rel="noopener noreferrer"
                className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 hover:border-[#002FA7]/30 hover:bg-[#EFF3FF]/30 transition-all group"
                data-testid={`news-link-${source.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <ExternalLink size={14} className="text-slate-300 group-hover:text-[#002FA7] mt-0.5 shrink-0 transition-colors" />
                <div>
                  <div className="text-sm font-medium text-slate-800 group-hover:text-[#002FA7] transition-colors">{source.name}</div>
                  <div className="text-xs text-slate-400 leading-relaxed">{source.desc}</div>
                </div>
              </a>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-7">
          <h2 className="text-xl font-['Cabinet_Grotesk'] font-bold mb-4 text-slate-900">How Much Do NY Investment Sales Agents Earn at Krieger?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-[#EFF3FF] border border-[#002FA7]/10 rounded-lg">
              <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-[#002FA7]">$27,000</div>
              <div className="text-xs text-slate-500 mt-1">You keep on a $1M sale<br />(3% commission, 90/10 split)</div>
            </div>
            <div className="text-center p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-400">$21,000</div>
              <div className="text-xs text-slate-500 mt-1">Traditional 70/30<br />on the same deal</div>
            </div>
            <div className="text-center p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-emerald-600">+$6,000</div>
              <div className="text-xs text-slate-500 mt-1">Extra per deal<br />with Krieger</div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <Link to="/agent-income-calculator" className="text-sm text-[#002FA7] border border-[#002FA7]/20 px-5 py-2 rounded-full hover:bg-[#EFF3FF] transition-colors mr-3">Calculate Your Earnings</Link>
          <Link to="/commission-plans" className="text-sm text-slate-600 border border-slate-200 px-5 py-2 rounded-full hover:bg-slate-50 transition-colors">View Commission Plans</Link>
        </div>
      </div>
    </SEOPageLayout>
  );
}
